import React from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaDownload, FaEye, FaLock, FaLockOpen, FaRegFile } from "react-icons/fa";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000FormStateHandlersReturn } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import { getIndexDocumentId, type U3141ChildLotChain, type U3141DocumentTypeEntry } from "../../../U3141Service";
import { NS, U3141ROLES } from "../../../U3141";

/** Handlers formulaire lot enfant + actions barre d’outils (verrouillage, index, etc.). */
export type U3141ChildLotChainFormStateHandlers = U2000FormStateHandlersReturn<U3141ChildLotChain> & {
  modifyChildLotChain: () => void;
  lockChildLotChain: () => void;
  unlockChildLotChain: () => void;
  fetchPropertyIndex: () => void;
  viewDocument: () => void;
};

// Typage des props du composant
interface U3141ChildLotChainFormBtnsComponentProps {
  formStateHandlers: U3141ChildLotChainFormStateHandlers;
  fetchPropertyIndexCooldown?: boolean;
}

// Composant principal pour la barre d'outils du formulaire de rôle
export const U3141ChildLotChainFormToolBar: React.FC<U3141ChildLotChainFormBtnsComponentProps> = ({ formStateHandlers, fetchPropertyIndexCooldown = false }) => {
  const formState = formStateHandlers.getFormState();
  const data = formStateHandlers.getData() || {};
  const lotDetail = data.lotDetail ?? null;
  const lotState = lotDetail?.lotState;
  const hasIndexDoc =
    lotDetail?.documentTypes?.some((e: U3141DocumentTypeEntry) => e.documentType === "rfLotDetailIndex") === true &&
    !!getIndexDocumentId(lotDetail);

  const { hasRole } = useU2000AuthContext();
  const { t } = useTranslation();

  const actions = [
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3141ROLES.EDIT),
      handleClick: () => {
        formStateHandlers.modifyChildLotChain();
      },
      disabled: formState === U2000FormStateEnumState.EDIT || lotState === "validate",
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "lock",
      label: t("btnLotDetail.lockLot", { ns: NS }),
      hasRole: hasRole(U3141ROLES.EDIT),
      disabled: formState === U2000FormStateEnumState.EDIT,
      handleClick: () => {
        formStateHandlers.lockChildLotChain();
      },
      icon: FaLock,
      visible: lotState !== "validate",
      Component: U2000ActionBtn,
    },
    {
      key: "unlock",
      label: t("btnLotDetail.unlockLot", { ns: NS }),
      hasRole: hasRole(U3141ROLES.EDIT),
      disabled: formState === U2000FormStateEnumState.EDIT,
      handleClick: () => {
        formStateHandlers.unlockChildLotChain();
      },
      icon: FaLockOpen,
      visible: lotState !== "notValidate",
      Component: U2000ActionBtn,
    },
    {
      key: "fetchPropertyIndex",
      label: t("btnLotDetail.fetchPropertyIndex", { ns: NS }),
      hasRole: hasRole(U3141ROLES.EDIT),
      disabled: formState === U2000FormStateEnumState.READ && lotState !== "validate" ? fetchPropertyIndexCooldown : true,
      handleClick: () => {
        formStateHandlers.fetchPropertyIndex();
      },
      icon: FaDownload,
      Component: U2000ActionBtn,
    },
    {
      key: "viewDocument",
      label: t("btnLotDetail.viewDocument", { ns: NS }),
      hasRole: hasRole(U3141ROLES.EDIT),
      disabled: !(lotState && hasIndexDoc),
      handleClick: () => {
        formStateHandlers.viewDocument();
      },
      icon: FaEye,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
